import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { attendanceAPI } from '../services/api';
import { Spinner } from '../components/Shared';
import '../styles/brutal.css';

const HISTORY_DAYS = 30;
const POINTS_PER_DAY = 10;
const PERFECT_WEEK_BONUS = 25;
const MEDALS = ['🥇', '🥈', '🥉'];

/* ---------------- date helpers (Mon–Fri work week) ---------------- */
const toKey = (d) => (d instanceof Date ? d.toISOString().slice(0, 10) : String(d).slice(0, 10));
const isWeekend = (d) => { const day = d.getDay(); return day === 0 || day === 6; };
const isoWeekKey = (d) => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  return `${date.getUTCFullYear()}-W${week}`;
};
const workdaysGoingBack = (fromDate, count) => {
  const out = [];
  const d = new Date(fromDate);
  while (out.length < count) {
    if (!isWeekend(d)) out.push(new Date(d));
    d.setDate(d.getDate() - 1);
  }
  return out;
};

/* ---------------- derive streak / points / badges from raw history ---------------- */
function buildStats(historyRows, userId, today) {
  const presentDates = new Set(
    historyRows.filter((r) => r.userId === userId && r.status === 'PRESENT').map((r) => toKey(r.date))
  );

  // Current streak: walk back through workdays from today; a missed
  // workday breaks it, weekends are skipped rather than breaking it.
  let streak = 0;
  const back = workdaysGoingBack(today, HISTORY_DAYS);
  for (const day of back) {
    if (presentDates.has(toKey(day))) streak += 1;
    else break;
  }

  // Best streak across the whole fetched window.
  let best = 0, run = 0;
  for (let i = back.length - 1; i >= 0; i -= 1) {
    if (presentDates.has(toKey(back[i]))) { run += 1; best = Math.max(best, run); }
    else run = 0;
  }

  // Perfect weeks: every workday tracked that week was present.
  const byWeek = new Map();
  back.forEach((day) => {
    const wk = isoWeekKey(day);
    if (!byWeek.has(wk)) byWeek.set(wk, { total: 0, present: 0 });
    const entry = byWeek.get(wk);
    entry.total += 1;
    if (presentDates.has(toKey(day))) entry.present += 1;
  });
  let perfectWeeks = 0;
  byWeek.forEach((v) => { if (v.total >= 3 && v.present === v.total) perfectWeeks += 1; });

  const presentCount = presentDates.size;
  const points = presentCount * POINTS_PER_DAY + perfectWeeks * PERFECT_WEEK_BONUS;

  return { streak, best, perfectWeeks, presentCount, points };
}

/* ---------------- small pieces ---------------- */
const Flame = ({ n }) => (
  <span className="fd-flame" title={`${n}-day streak`}>
    🔥<span>{n}</span>
  </span>
);

function LeaderRow({ rank, member, onCheckIn, canCheckIn, justChecked }) {
  return (
    <div className={`fd-leader-row ${rank < 3 ? 'top' : ''} ${justChecked ? 'pop' : ''}`}>
      <span className="fd-leader-rank">{MEDALS[rank] || rank + 1}</span>
      <span className="fd-avatar">{member.initials}</span>
      <span className="fd-leader-name">
        {member.memberName}
        <span className="fd-leader-role">{member.jobTitle}</span>
      </span>
      <Flame n={member.streak} />
      <span className="fd-points">{member.points} pts</span>
      <span className={`fd-status-pill ${member.status}`}>{member.status === 'PRESENT' ? 'In' : 'Out'}</span>
      {canCheckIn && (
        <button className="fd-btn small pink" onClick={() => onCheckIn(member.userId)}>
          Check in
        </button>
      )}
    </div>
  );
}

/* ---------------- page ---------------- */
export default function Attendance() {
  const { isAdmin, user } = useAuth();
  const currentUserId = user?.userId ?? user?.id ?? null;

  const [today, setTodayRows] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [busyId, setBusyId] = useState(null);
  const [justChecked, setJustChecked] = useState(null);
  const [toast, setToast] = useState({ msg: '', show: false });

  const showToast = useCallback((msg) => {
    setToast({ msg, show: true });
    setTimeout(() => setToast((t) => ({ ...t, show: false })), 2400);
  }, []);

  const load = useCallback(() => {
    Promise.all([attendanceAPI.today(), attendanceAPI.history(HISTORY_DAYS)])
      .then(([t, h]) => {
        setTodayRows(t.data || []);
        setHistory(h.data || []);
      })
      .catch((err) => {
        console.error('Failed to load attendance:', err);
        showToast('Could not load attendance. Check your connection and refresh.');
      })
      .finally(() => setLoading(false));
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const checkIn = async (userId) => {
    setBusyId(userId);
    try {
      await attendanceAPI.mark({ userId, status: 'PRESENT' });
      setJustChecked(userId);
      setTimeout(() => setJustChecked(null), 700);
      showToast(userId === currentUserId ? "You're checked in!" : 'Marked present');
      load();
    } catch (err) {
      if (err?.response?.status === 409) showToast('Already checked in for today');
      else showToast('Could not save attendance. Try again.');
    } finally {
      setBusyId(null);
    }
  };

  const now = useMemo(() => new Date(), []);

  const roster = useMemo(() => {
    return today
      .map((row) => {
        const stats = buildStats(history, row.userId, now);
        return {
          ...row,
          ...stats,
          initials: (row.memberName || '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()
        };
      })
      .sort((a, b) => b.points - a.points || b.streak - a.streak);
  }, [today, history, now]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return roster.filter((m) => {
      const textOk = !q || [m.memberName, m.memberEmail, m.jobTitle].some((v) => (v || '').toLowerCase().includes(q));
      const statusOk = statusFilter === 'ALL' || m.status === statusFilter;
      return textOk && statusOk;
    });
  }, [roster, search, statusFilter]);

  const summary = useMemo(() => {
    const presentCount = today.filter((r) => r.status === 'PRESENT').length;
    const total = today.length;
    const longestStreak = roster.reduce((max, m) => Math.max(max, m.best), 0);
    const streaksOnFire = roster.filter((m) => m.streak >= 3).length;
    return { presentCount, total, pct: total ? Math.round((presentCount / total) * 100) : 0, longestStreak, streaksOnFire };
  }, [today, roster]);

  const selfRow = roster.find((m) => m.userId === currentUserId);
  const selfMarkedToday = selfRow?.status === 'PRESENT';

  if (loading) return <Spinner />;

  return (
    <div className="fd-page">
      <div className="fd-head">
        <div>
          <h1 className="fd-title">Attendance<span className="fd-title-mark">Streak board</span></h1>
          <p className="fd-sub">
            {now.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} · check in once, keep the flame alive.
          </p>
        </div>
        <div className="fd-head-actions">
          {currentUserId != null && (
            <button
              className="fd-btn pink"
              disabled={selfMarkedToday || busyId === currentUserId}
              onClick={() => checkIn(currentUserId)}
            >
              {selfMarkedToday ? "You're checked in ✓" : busyId === currentUserId ? 'Checking in…' : 'Check in now'}
            </button>
          )}
        </div>
      </div>

      <div className="fd-stats">
        <div className="fd-stat"><div className="fd-stat-num">{summary.presentCount}/{summary.total}</div><div className="fd-stat-label">Present today</div></div>
        <div className="fd-stat"><div className="fd-stat-num">{summary.pct}%</div><div className="fd-stat-label">Turnout</div></div>
        <div className="fd-stat"><div className="fd-stat-num">{summary.longestStreak}</div><div className="fd-stat-label">Longest streak (30d)</div></div>
        <div className="fd-stat"><div className="fd-stat-num">{summary.streaksOnFire}</div><div className="fd-stat-label">On a 3+ day streak</div></div>
      </div>

      <div className="fd-toolbar">
        <div className="fd-search">
          <input className="fd-input" placeholder="Search team members" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search team" />
        </div>
        {['ALL', 'PRESENT', 'ABSENT'].map((s) => (
          <button key={s} className={`fd-chip ${statusFilter === s ? 'on' : ''}`} onClick={() => setStatusFilter(s)}>
            {s === 'ALL' ? 'Everyone' : s === 'PRESENT' ? 'In today' : 'Out today'}
          </button>
        ))}
      </div>

      <div className="fd-panel">
        <div className="fd-panel-head">
          <div>
            <h3 className="fd-panel-title">Leaderboard</h3>
            <span className="fd-panel-note">Ranked by points · {POINTS_PER_DAY} pts a day present, +{PERFECT_WEEK_BONUS} for a perfect week</span>
          </div>
        </div>

        <div className="fd-leader-head">
          <span></span><span></span><span>Member</span><span>Streak</span><span>Points</span><span>Today</span><span></span>
        </div>

        {!filtered.length ? (
          <div className="fd-empty">No one matches your filters.</div>
        ) : (
          filtered.map((m) => (
            <LeaderRow
              key={m.userId}
              rank={roster.indexOf(m)}
              member={m}
              justChecked={justChecked === m.userId}
              canCheckIn={m.status !== 'PRESENT' && (isAdmin || m.userId === currentUserId)}
              onCheckIn={checkIn}
            />
          ))
        )}
      </div>

      <div className={`fd-toast ${toast.show ? 'show' : ''}`} role="status">{toast.msg}</div>
    </div>
  );
}